/**
 * Per-player list selection bottom sheet presentation.
 *
 * Composed on the shared MobileSheetChrome primitive (SE-5525), which owns
 * the sheet surface, drag handle, ✕ close chip, height cap, and every
 * dismissal path. This view supplies the content: the "Select list to add
 * <player>" heading (sticky header), a checkbox list of watchlists with
 * player counts plus a toggleable inline "create new list" row (scrolling
 * body), and a Cancel/Confirm sticky footer.
 * @module features/scouting/components/SelectListSheet/SelectListSheetView
 */

import { Plus, XIcon } from "lucide-react-native";
import React, { memo } from "react";

import MobileSheetChrome from "@/components/molecules/MobileSheetChrome";
import { Divider } from "@/components/atoms/Divider";
import { Heading } from "@/components/atoms/Heading";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Input } from "@/components/atoms/Input";
import { Pressable } from "@/components/atoms/Pressable";
import { Spinner } from "@/components/atoms/Spinner";
import { Text } from "@/components/atoms/Text";

import SelectListRow from "@/features/scouting/components/molecules/SelectListRow";
import { WATCHLIST_NAME_MAX } from "@/features/scouting/utils/watchlistNameRules";

/** A single watchlist item for display */
export interface SelectListItem {
  /** Watchlist ID */
  readonly id: string;
  /** Watchlist display name */
  readonly name: string;
  /** Number of players in this watchlist */
  readonly playerCount: number;
  /** Whether the user can edit (rename) this watchlist */
  readonly canEdit?: boolean;
  /** Whether the user can delete this watchlist */
  readonly canDelete?: boolean;
}

/** Props for SelectListSheetView */
interface SelectListSheetViewProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly playerName: string;
  readonly items: readonly SelectListItem[];
  readonly checkedIds: readonly string[];
  readonly onToggle: (watchlistId: string) => void;
  readonly isSaving: boolean;
  readonly onCreateList: () => void;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
  readonly newListName: string;
  readonly onNewListNameChange: (text: string) => void;
  /** Inline message from the shared name rule (SE-8449), or null. */
  readonly newListNameError: string | null;
  /** Whether the typed name passes the shared name rule; gates Add. */
  readonly isNewListNameValid: boolean;
  readonly onClearNewListName: () => void;
  readonly isCreating: boolean;
  /** Whether the inline create form is visible */
  readonly isCreateFormVisible: boolean;
  /** Shows the inline create form */
  readonly onShowCreateForm: () => void;
  /** Pre-built edit handlers keyed by watchlist ID */
  readonly editHandlers: Readonly<Record<string, () => void>>;
  /** Pre-built delete handlers keyed by watchlist ID */
  readonly deleteHandlers: Readonly<Record<string, () => void>>;
  /** Pre-built share handlers keyed by watchlist ID */
  readonly shareHandlers: Readonly<Record<string, () => void>>;
}

/**
 * Dismissal no-op used while a save is in flight — the chrome requires a
 * close handler, but a mid-save dismissal must not fire the consumer's
 * onClose (which resets local toggle state).
 */
const IGNORE_CLOSE_WHILE_SAVING = () => undefined;

/**
 * Renders the per-player list selection bottom sheet
 * @param root0 - View props
 * @param root0.isOpen - Whether sheet is visible
 * @param root0.onClose - Close handler
 * @param root0.playerName - Player name to display in header
 * @param root0.items - Watchlist items with player counts
 * @param root0.checkedIds - IDs of currently checked watchlists
 * @param root0.onToggle - Toggle handler for a watchlist
 * @param root0.isSaving - Whether save is in progress
 * @param root0.onCreateList - Inline create list handler
 * @param root0.onCancel - Cancel handler
 * @param root0.onConfirm - Confirm handler
 * @param root0.newListName - New list name input value
 * @param root0.onNewListNameChange - New list name change handler
 * @param root0.newListNameError - Inline message from the shared name rule
 * @param root0.isNewListNameValid - Whether the typed name passes the rule
 * @param root0.onClearNewListName - Clear/hide create form handler
 * @param root0.isCreating - Whether create-list mutation is in progress
 * @param root0.isCreateFormVisible - Whether the create form is showing
 * @param root0.onShowCreateForm - Show the create form
 * @param root0.editHandlers - Pre-built edit handlers keyed by watchlist ID
 * @param root0.deleteHandlers - Pre-built delete handlers keyed by watchlist ID
 * @param root0.shareHandlers - Pre-built share handlers keyed by watchlist ID
 */
const SelectListSheetView = ({
  isOpen,
  onClose,
  playerName,
  items,
  checkedIds,
  onToggle,
  isSaving,
  onCreateList,
  onCancel,
  onConfirm,
  newListName,
  onNewListNameChange,
  newListNameError,
  isNewListNameValid,
  onClearNewListName,
  isCreating,
  isCreateFormVisible,
  onShowCreateForm,
  editHandlers,
  deleteHandlers,
  shareHandlers,
}: SelectListSheetViewProps) => (
  <MobileSheetChrome
    isOpen={isOpen}
    // Dismissal is ignored while a save is in flight (pre-SE-5525 the raw
    // Actionsheet received onClose={undefined} while saving).
    onClose={isSaving ? IGNORE_CLOSE_WHILE_SAVING : onClose}
    headerContent={
      <Heading variant="title-sm">
        Select list to add{" "}
        <Text variant="body" className="italic text-content-muted">
          {playerName}
        </Text>
      </Heading>
    }
    footer={
      <Stack direction="horizontal" className="w-full" space="3">
        <Pressable
          onPress={onCancel}
          disabled={isSaving}
          accessibilityRole="button"
          className="h-10 items-center justify-center rounded-none border border-surface-muted px-8"
        >
          <Text variant="title-sm-medium" className="text-content-muted">
            Cancel
          </Text>
        </Pressable>
        <Pressable
          onPress={onConfirm}
          disabled={isSaving}
          accessibilityRole="button"
          className="h-10 flex-1 items-center justify-center rounded-none bg-accent-primary px-5"
        >
          {isSaving ? (
            <Spinner size="small" className="text-content-on-accent" />
          ) : (
            <Text variant="title-sm-medium" className="text-content-on-accent">
              Confirm Selection
            </Text>
          )}
        </Pressable>
      </Stack>
    }
  >
    <Stack className="w-full">
      {items.map(item => (
        <SelectListRow
          key={item.id}
          id={item.id}
          name={item.name}
          playerCount={item.playerCount}
          isChecked={checkedIds.includes(item.id)}
          onToggle={onToggle}
          onShare={shareHandlers[item.id]}
          onEdit={editHandlers[item.id]}
          onDelete={deleteHandlers[item.id]}
        />
      ))}
    </Stack>

    <Stack className="py-1">
      <Divider />
    </Stack>

    {isCreateFormVisible ? (
      <Stack className="w-full gap-1 py-3">
        <Stack direction="horizontal" className="w-full items-center gap-3">
          <Input
            variant="outline"
            size="sm"
            className="flex-1 rounded-sm border-outline-strong"
            field={{
              placeholder: "New list name...",
              value: newListName,
              onChangeText: onNewListNameChange,
              editable: !isCreating,
              maxLength: WATCHLIST_NAME_MAX,
            }}
          />
          <Pressable
            onPress={onCreateList}
            disabled={!isNewListNameValid || isCreating}
            accessibilityRole="button"
            className="h-9 items-center justify-center rounded-none bg-accent-primary px-4"
          >
            {isCreating ? (
              <Spinner size="small" className="text-content-on-accent" />
            ) : (
              <Text variant="body-strong" className="text-content-on-accent">
                Add
              </Text>
            )}
          </Pressable>
          <Pressable
            onPress={onClearNewListName}
            className="size-9 items-center justify-center rounded-none bg-surface-subtle"
          >
            <Icon as={XIcon} size="sm" className="text-content-subtle" />
          </Pressable>
        </Stack>
        {newListNameError ? (
          <Text variant="micro" className="text-status-error">
            {newListNameError}
          </Text>
        ) : null}
      </Stack>
    ) : (
      <Pressable onPress={onShowCreateForm} className="w-full py-3">
        <Stack direction="horizontal" className="items-center" space="2">
          <Icon
            as={Plus}
            size="sm"
            className="text-needs-semantic-primary-500"
          />
          <Text variant="body" className="text-needs-semantic-primary-500">
            Create new list
          </Text>
        </Stack>
      </Pressable>
    )}
  </MobileSheetChrome>
);

SelectListSheetView.displayName = "SelectListSheetView";

export default memo(SelectListSheetView);
